import React, { useEffect, useState } from 'react';
import { subscribeToSession } from '../sessionService';
import { Session } from '../types';

interface WaitingScreenProps {
  sessionCode: string;
  userName: string;
  onPartnerJoined: (session: Session) => void;
}

const WaitingScreen: React.FC<WaitingScreenProps> = ({ sessionCode, userName, onPartnerJoined }) => {
  const [copied, setCopied] = useState(false);
  const [dots, setDots] = useState('');

  useEffect(() => {
    const unsubscribe = subscribeToSession(sessionCode, (session) => {
      if (session.status === 'active' && session.userB !== null) {
        onPartnerJoined(session);
      }
    });
    return () => unsubscribe();
  }, [sessionCode, onPartnerJoined]);

  useEffect(() => {
    const interval = setInterval(() => {
      setDots(d => d.length >= 3 ? '' : d + '.');
    }, 500);
    return () => clearInterval(interval);
  }, []);

  const handleCopy = () => {
    navigator.clipboard.writeText(sessionCode);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleShare = async () => {
    const shareData = {
      title: 'Dinder - Let\'s pick dinner!',
      text: `Join my Dinder session! Use code ${sessionCode} to swipe on restaurants together.`,
      url: `https://dinderapp.com`,
    };
    if (navigator.share) {
      try {
        await navigator.share(shareData);
      } catch (err) {
        handleCopy();
      }
    } else {
      handleCopy();
    }
  };

  return (
    <div className="flex flex-col items-center justify-center min-h-screen px-6 py-12 bg-dinder-blue">
      <div className="w-full max-w-sm text-center">

        <div className="text-7xl mb-6">⏳</div>
        <h2 className="text-3xl font-extrabold text-white mb-2">Waiting for your partner{dots}</h2>
        <p className="text-blue-100 mb-10">Share this code so they can join</p>

        {/* Session code */}
        <div
          onClick={handleCopy}
          className="bg-white/20 border-2 border-white/50 rounded-3xl px-8 py-6 mb-4 cursor-pointer active:scale-95 transition-all"
        >
          <p className="text-6xl font-extrabold text-white tracking-widest">{sessionCode}</p>
        </div>

        {/* Share button - primary CTA */}
        <button
          onClick={handleShare}
          className="w-full bg-dinder-pink text-white font-bold text-lg py-4 rounded-2xl mb-3 transition-all active:scale-95 flex items-center justify-center gap-2"
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="18" cy="5" r="3"/><circle cx="6" cy="12" r="3"/><circle cx="18" cy="19" r="3"/>
            <line x1="8.59" y1="13.51" x2="15.42" y2="17.49"/>
            <line x1="15.41" y1="6.51" x2="8.59" y2="10.49"/>
          </svg>
          Share Code with Partner
        </button>

        <button
          onClick={handleCopy}
          className="w-full bg-white/20 border border-white/30 text-white font-semibold text-base py-3 rounded-2xl mb-10 transition-all active:scale-95"
        >
          {copied ? '✓ Copied!' : 'Copy Code Only'}
        </button>

        {/* How it works */}
        <div className="bg-white/15 rounded-2xl p-5 text-left space-y-3">
          <p className="text-white/60 font-semibold text-xs uppercase tracking-widest mb-3">How this works</p>
          {[
            { icon: (
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="5" y="2" width="14" height="20" rx="2" ry="2"/><line x1="12" y1="18" x2="12.01" y2="18"/>
              </svg>
            ), text: 'Your partner opens Dinder on their phone' },
            { icon: (
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
              </svg>
            ), text: `They tap "Join a Session" and enter code ${sessionCode}` },
            { icon: (
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M14 9V5a3 3 0 0 0-3-3l-4 9v11h11.28a2 2 0 0 0 2-1.7l1.38-9a2 2 0 0 0-2-2.3H14z"/>
                <path d="M7 22H4a2 2 0 0 1-2-2v-7a2 2 0 0 1 2-2h3"/>
              </svg>
            ), text: 'You each swipe through restaurants independently' },
            { icon: (
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="20 12 20 22 4 22 4 12"/><rect x="2" y="7" width="20" height="5"/>
                <line x1="12" y1="22" x2="12" y2="7"/><path d="M12 7H7.5a2.5 2.5 0 0 1 0-5C11 2 12 7 12 7z"/>
                <path d="M12 7h4.5a2.5 2.5 0 0 0 0-5C13 2 12 7 12 7z"/>
              </svg>
            ), text: 'See all your matches revealed together at the end' },
          ].map((item, i) => (
            <div key={i} className="flex items-start gap-3">
              <div className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                {item.icon}
              </div>
              <p className="text-white/70 text-sm">{item.text}</p>
            </div>
          ))}
        </div>

        <p className="text-white/30 text-xs mt-6">Session expires in 2 hours · Code: {sessionCode}</p>

      </div>
    </div>
  );
};

export default WaitingScreen;